import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const cta = user ? "/app" : "/register";

  return (
    <div className="min-h-screen parchment">
      <header className="max-w-6xl mx-auto px-6 lg:px-10 py-6 flex justify-between items-center">
        <div className="font-serif text-xl tracking-tight">Answering the Call</div>
        <div className="flex items-center gap-3">
          {user ? (
            <Link to="/app"><Button data-testid="header-app-btn" size="sm">Open App</Button></Link>
          ) : (
            <>
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="header-login-link">Sign in</Link>
              <Link to="/register"><Button data-testid="header-register-btn" size="sm">Begin</Button></Link>
            </>
          )}
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-12 pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 reveal">
            <p className="text-sm uppercase tracking-[0.2em] text-primary mb-6">An eleven-week disciplemaking journey</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-balance">
              He didn't call us <br />to fill a pew.<br />
              <span className="italic">He called us to <span className="underline-serif">follow.</span></span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-muted-foreground leading-relaxed">
              Discipleship is the act of <em>trusting</em>, <em>following</em>, <em>imitating</em> Jesus, and
              teaching others to do the same. Over eleven weeks — with weekly readings, honest questions,
              a guided journal, and grace-filled AI reflection — you'll grow as a disciple and be equipped
              to disciple someone else.
            </p>
            <div className="mt-10 flex gap-4">
              <Button size="lg" data-testid="hero-begin-btn" onClick={() => nav(cta)} className="rounded-full px-8">
                {user ? "Continue the journey" : "Begin the journey"}
              </Button>
              <Button size="lg" variant="ghost" data-testid="hero-learn-btn" onClick={() => document.getElementById("movements").scrollIntoView({ behavior: "smooth" })}>
                What you'll walk through →
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5 lg:pl-6">
            <div className="border border-border bg-card p-8 lg:p-10 shadow-[0_8px_30px_rgba(42,42,38,0.06)]">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">The Gospel in Five Words</div>
              <div className="font-serif text-3xl leading-tight">
                Died. <br /> Buried. <br /> Risen. <br /> Seen. <br /> <span className="text-primary">Received.</span>
              </div>
              <p className="mt-6 text-sm text-muted-foreground">
                Not a legend we hope is true; history we can stand on. And because it's true, there is only
                one right response: receive it, hold onto it, and let it hold you.
              </p>
              <div className="rule-line my-6"></div>
              <div className="text-xs italic text-muted-foreground">— 1 Corinthians 15:1–6</div>
            </div>
          </div>
        </div>
      </section>

      <section id="movements" className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <p className="text-sm uppercase tracking-[0.2em] text-primary mb-4">Four movements. Eleven weeks.</p>
          <h2 className="font-serif text-4xl sm:text-5xl max-w-3xl leading-tight">
            Trust. Follow. Imitate. Teach others.
          </h2>
          <p className="mt-6 max-w-2xl text-muted-foreground">
            The order matters. You cannot follow someone you don't trust. You cannot imitate someone
            you're not following closely. And you have nothing lasting to teach until trusting,
            following, and imitating have actually begun to shape your own life.
          </p>

          <div className="mt-16 grid md:grid-cols-2 gap-x-16 gap-y-12">
            {[
              { n: "01", t: "Trust", w: "Weeks 1–3", d: "Agreement changes your mind. Trust changes everything — even your character, especially in the private places." },
              { n: "02", t: "Follow", w: "Weeks 4–6", d: "Count the cost with eyes open. Follow Him in prayer as a rhythm, and in His Word for transformation." },
              { n: "03", t: "Imitate", w: "Weeks 7–9", d: "Become a mirror of Christ — in relationships, in leadership, in the ordinary places others watch you." },
              { n: "04", t: "Teach Others", w: "Weeks 10–11", d: "Tell your story simply. Carry the gospel plainly. Then hand this same handbook to your Timothy." },
            ].map((m, i) => (
              <div key={m.n} className="reveal" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="flex items-baseline gap-4 mb-3">
                  <span className="font-serif text-3xl text-primary">{m.n}</span>
                  <span className="font-serif text-3xl">{m.t}</span>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground ml-auto">{m.w}</span>
                </div>
                <p className="text-muted-foreground leading-relaxed pl-14">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-secondary/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-primary mb-4">Built for the way you actually grow</p>
            <h2 className="font-serif text-4xl leading-tight">A journal, a group, and a coach — <em>all in one place.</em></h2>
          </div>
          <ul className="space-y-6">
            {[
              ["Weekly readings", "Key truths, opening scripture, discussion questions, weekly practice, memory verse, and closing prayer for every week."],
              ["A guided journal", "Reflect on each week's questions privately. Read them back later and watch how you've grown."],
              ["AI Reflection Coach", "A grace-filled voice that reads your reflection and asks one thoughtful question in response — never preachy."],
              ["Small groups & leaders", "Leaders create a group, share a code, and walk their people through the same eleven weeks together."],
              ["A covenant to close", "When you finish, sign the covenant and name your Timothy — the person you'll walk through this next."],
            ].map(([t, d], i) => (
              <li key={t} className="flex gap-5">
                <span className="font-serif text-xl text-primary tabular-nums pt-1">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <div className="font-medium">{t}</div>
                  <div className="text-sm text-muted-foreground mt-1">{d}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight">
            The call is going out. <br /> <em className="text-primary">Answer it.</em>
          </h2>
          <p className="mt-6 text-muted-foreground">
            Sign up as a participant or as a leader. Walk the eleven weeks. Then walk it again with someone new.
          </p>
          <div className="mt-10">
            <Button size="lg" data-testid="cta-begin-btn" onClick={() => nav(cta)} className="rounded-full px-10">
              {user ? "Continue" : "Create your account"}
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 text-xs text-muted-foreground flex flex-col sm:flex-row justify-between gap-2">
          <div>Answering the Call — an eleven-week disciplemaking training program.</div>
          <div className="font-serif italic">Trust · Follow · Imitate · Teach Others</div>
        </div>
      </footer>
    </div>
  );
}
